// slices/providerSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ProviderService from "../services/ProviderService";
import { tokenHelpers } from '../api/api';

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  // Check if error is already a string
  if (typeof error === 'string') {
    errorMessage = error;
  }
  // Check if error has data property (your API response structure)
  else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  }
  // Check if error has direct error property
  else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  }
  // Check if error has message property
  else if (error?.message) {
    errorMessage = error.message;
  }
  // Check response structure
  else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  }
  else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  }
  else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  }
  else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  }
  // Check if error has success: false structure
  else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  }
  // Check if error has statusCode and data
  else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  }
  // Check if error has response but no data
  else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  }
  // Check if error is an object with error property
  else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

// Get all service providers with filtering, sorting, and pagination
export const getAllProviders = createAsyncThunk(
  'provider/getAllProviders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getAllProviders(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get service provider by ID
export const getProviderById = createAsyncThunk(
  'provider/getProviderById',
  async ({ id, populate = null }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProviderById(id, populate);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get service providers by service type
export const getProvidersByServiceType = createAsyncThunk(
  'provider/getProvidersByServiceType',
  async ({ serviceType, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProvidersByServiceType(serviceType, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get featured service providers
export const getFeaturedProviders = createAsyncThunk(
  'provider/getFeaturedProviders',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getFeaturedProviders(page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get service providers with random sorting
export const getRandomProviders = createAsyncThunk(
  'provider/getRandomProviders',
  async ({ page = 1, limit = 10, filter = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getRandomProviders(page, limit, filter);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get nearby providers
export const getNearbyProviders = createAsyncThunk(
  'provider/getNearbyProviders',
  async ({ latitude, longitude, maxDistance = 10, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getNearbyProviders(
        latitude,
        longitude,
        maxDistance,
        page,
        limit,
        filters
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    } 
  }
);

// Get nearby providers from request (IP-based)
export const getNearbyProvidersFromRequest = createAsyncThunk(
  'provider/getNearbyProvidersFromRequest',
  async ({ maxDistance = 10, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getNearbyProvidersFromRequest(
        maxDistance,
        page,
        limit,
        filters
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get providers by city
export const getProvidersByCity = createAsyncThunk(
  'provider/getProvidersByCity',
  async ({ city, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProvidersByCity(city, page, limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get providers by country
export const getProvidersByCountry = createAsyncThunk(
  'provider/getProvidersByCountry',
  async ({ country, page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProvidersByCountry(country, page, limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get providers in Haitian diaspora communities
export const getDiasporaProviders = createAsyncThunk(
  'provider/getDiasporaProviders',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getDiasporaProviders(page, limit, filters);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get providers by language they speak
export const getProvidersByLanguage = createAsyncThunk(
  'provider/getProvidersByLanguage',
  async ({ language, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProvidersByLanguage(language, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get providers by multiple languages
export const getProvidersByLanguages = createAsyncThunk(
  'provider/getProvidersByLanguages',
  async ({ languages, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProvidersByLanguages(languages, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get location info
export const getLocationInfo = createAsyncThunk(
  'provider/getLocationInfo',
  async ({ latitude, longitude }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getLocationInfo(latitude, longitude);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PROVIDER ROUTES (Requires Provider Authentication) --------

// Create a new service provider profile
export const createProviderProfile = createAsyncThunk(
  'create/createProvider',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ProviderService.createProvider(data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Update own service provider profile
export const updateProvider = createAsyncThunk(
  'provider/updateProvider',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.updateProvider(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Update own provider activity timestamp
export const updateProviderActivity = createAsyncThunk(
  'provider/updateProviderActivity',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ProviderService.updateProviderActivity(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get current user's provider profile
export const getMyProvider = createAsyncThunk(
  'provider/getMyProvider',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getMyProvider();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Update current user's provider profile
export const updateMyProvider = createAsyncThunk(
  'provider/updateMyProvider',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ProviderService.updateMyProvider(data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Delete provider profile
export const deleteProvider = createAsyncThunk(
  'provider/deleteProvider',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ProviderService.deleteProvider(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Toggle featured status
export const toggleFeatured = createAsyncThunk(
  'provider/toggleFeatured',
  async ({ id, isFeatured, duration = 30 }, { rejectWithValue }) => {
    try {
      const response = await ProviderService.toggleFeatured(id, isFeatured, duration);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// Get provider statistics
export const getProviderStatistics = createAsyncThunk(
  'provider/getProviderStatistics',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ProviderService.getProviderStatistics(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Data states
  providers: [],
  currentProvider: null,
  featuredProviders: [],
  nearbyProviders: [],
  diasporaProviders: [],
  locationInfo: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  },
  
  // Filter/Search
  filters: {},
  searchQuery: '',
  
  // Loading states
  loading: false,
  creating: false,
  updating: false,
  
  // Error states
  error: null,
  createError: null,
  updateError: null,
  
  // Success states
  createSuccess: false,
  updateSuccess: false,
  
  // UI states
  isRefreshing: false,
  hasMore: true,
  selectedProviderId: null
};

const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    // Reset ALL provider state
    resetProviderState: (state) => {
      state.providers = [];
      state.currentProvider = null;
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.createSuccess = false;
      state.updateSuccess = false;
      state.creating = false;
      state.updating = false;
    },
    
    // Reset create states (success and error)
    resetCreateState: (state) => {
      state.createSuccess = false;
      state.createError = null;
      state.creating = false;
    },
    
    // Reset update states
    resetUpdateState: (state) => {
      state.updateSuccess = false;
      state.updateError = null;
      state.updating = false;
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
    },
    
    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
      state.searchQuery = '';
    },
    
    // Set search query
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    
    // Set pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set refreshing
    setRefreshing: (state, action) => {
      state.isRefreshing = action.payload;
    },
    
    // Select provider
    selectProvider: (state, action) => {
      state.selectedProviderId = action.payload;
    },
    
    // Clear current provider
    clearCurrentProvider: (state) => {
      state.currentProvider = null;
    },
    
    // Update provider in list
    updateProviderInList: (state, action) => {
      const updatedProvider = action.payload;
      const index = state.providers.findIndex(p => p._id === updatedProvider._id);
      if (index !== -1) {
        state.providers[index] = updatedProvider;
      }
    },
    
    // Remove provider from list
    removeProviderFromList: (state, action) => {
      const id = action.payload;
      state.providers = state.providers.filter(p => p._id !== id);
    },
    
    updateCurrentProvider: (state, action) => {
      const updatedData = action.payload;
      if (state.currentProvider) {
        state.currentProvider = { ...state.currentProvider, ...updatedData };
        
        // Also update in providers list
        const index = state.providers.findIndex(p => p._id === state.currentProvider._id);
        if (index !== -1) {
          state.providers[index] = { ...state.providers[index], ...updatedData };
        }
        
        // ✅ Update localStorage
        try {
          const userDataStr = localStorage.getItem('user_data');
          if (userDataStr) {
            const userData = JSON.parse(userDataStr);
            const updatedUserData = {
              ...userData,
              providerProfile: {
                ...(userData.providerProfile || {}),
                ...updatedData
              }
            };
            localStorage.setItem('user_data', JSON.stringify(updatedUserData));
            console.log('✅ Provider data updated in localStorage:', updatedData);
          }
        } catch (error) {
          console.error('❌ Failed to update provider data in localStorage:', error);
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========== GET ALL PROVIDERS ==========
      .addCase(getAllProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isRefreshing = true;
      })
      .addCase(getAllProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        
        const responseData = action.payload;
        
        console.log('🔍 Processing provider data:', responseData);
        
        if (responseData?.data?.items && Array.isArray(responseData.data.items)) {
          state.providers = responseData.data.items;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else if (Array.isArray(responseData)) {
          state.providers = responseData;
        } else {
          state.providers = [];
        }
        
        console.log(`✅ Loaded ${state.providers.length} providers`);
        
        if (responseData?.data?.pagination) {
          state.pagination = responseData.data.pagination;
        } else {
          state.pagination = {
            total: state.providers.length,
            page: 1,
            limit: 50,
            totalPages: Math.ceil(state.providers.length / 50)
          };
        }
        
        state.hasMore = state.pagination.page < state.pagination.totalPages;
      })
      .addCase(getAllProviders.rejected, (state, action) => {
        state.loading = false;
        state.isRefreshing = false;
        state.error = action.payload || 'Failed to fetch providers';
        console.error('❌ Failed to fetch providers:', state.error);
      })
      
      // ========== GET PROVIDER BY ID ==========
      .addCase(getProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProviderById.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data) {
          state.currentProvider = responseData.data;
        } else {
          state.currentProvider = responseData;
        }
      })
      .addCase(getProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch provider';
      })
      
      // ========== GET PROVIDERS BY SERVICE TYPE ==========
      .addCase(getProvidersByServiceType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProvidersByServiceType.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
        state.hasMore = state.pagination.page < state.pagination.totalPages;
      })
      .addCase(getProvidersByServiceType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by service type';
      })
      
      // ========== GET FEATURED PROVIDERS ==========
      .addCase(getFeaturedProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeaturedProviders.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.featuredProviders = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.featuredProviders = responseData.data;
        } else {
          state.featuredProviders = [];
        }
      })
      .addCase(getFeaturedProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch featured providers';
      })
      
      // ========== GET RANDOM PROVIDERS ==========
      .addCase(getRandomProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRandomProviders.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
      })
      .addCase(getRandomProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch random providers';
      })
      
      // ========== GET NEARBY PROVIDERS ==========
      .addCase(getNearbyProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNearbyProviders.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.nearbyProviders = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.nearbyProviders = responseData.data;
        } else {
          state.nearbyProviders = [];
        }
        state.hasMore = state.pagination.page < state.pagination.totalPages;
      })
      .addCase(getNearbyProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch nearby providers';
      })
      
      // ========== GET NEARBY PROVIDERS FROM REQUEST ==========
      .addCase(getNearbyProvidersFromRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNearbyProvidersFromRequest.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.nearbyProviders = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.nearbyProviders = responseData.data;
        } else {
          state.nearbyProviders = [];
        }
      })
      .addCase(getNearbyProvidersFromRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch nearby providers from request';
      })
      
      // ========== GET PROVIDERS BY CITY ==========
      .addCase(getProvidersByCity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProvidersByCity.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
      })
      .addCase(getProvidersByCity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by city';
      })
      
      // ========== GET PROVIDERS BY COUNTRY ==========
      .addCase(getProvidersByCountry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProvidersByCountry.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
      })
      .addCase(getProvidersByCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by country';
      })
      
      // ========== GET DIASPORA PROVIDERS ==========
      .addCase(getDiasporaProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDiasporaProviders.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.diasporaProviders = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.diasporaProviders = responseData.data;
        } else {
          state.diasporaProviders = [];
        }
      })
      .addCase(getDiasporaProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch diaspora providers';
      })
      
      // ========== GET PROVIDERS BY LANGUAGE ==========
      .addCase(getProvidersByLanguage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProvidersByLanguage.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
      })
      .addCase(getProvidersByLanguage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by language';
      })
      
      // ========== GET PROVIDERS BY LANGUAGES ==========
      .addCase(getProvidersByLanguages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProvidersByLanguages.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload;
        if (responseData?.data?.items) {
          state.providers = responseData.data.items;
          state.pagination = responseData.data.pagination || initialState.pagination;
        } else if (Array.isArray(responseData?.data)) {
          state.providers = responseData.data;
        } else {
          state.providers = [];
        }
      })
      .addCase(getProvidersByLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch providers by languages';
      })
      
      // ========== GET LOCATION INFO ==========
      .addCase(getLocationInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLocationInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.locationInfo = action.payload?.data || null;
      })
      .addCase(getLocationInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch location info';
      })
      
      // ========== CREATE PROVIDER ==========
      .addCase(createProviderProfile.pending, (state) => {
        state.creating = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createProviderProfile.fulfilled, (state, action) => {
        state.creating = false;
        state.createSuccess = true;
        state.currentProvider = action.payload?.data || null;
        if (action.payload?.data) {
          state.providers.unshift(action.payload.data);
          
          // ✅ UPDATE USER DATA IN LOCAL STORAGE
          try {
            const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
            const updatedUserData = {
              ...userData,
              providerProfile: action.payload.data,
              isProvider: true,
              providerId: action.payload.data._id
            };
            localStorage.setItem('user_data', JSON.stringify(updatedUserData));
            console.log('✅ User data updated with provider profile:', updatedUserData);
          } catch (error) {
            console.error('❌ Failed to update user data in localStorage:', error);
          }
        }
        console.log('✅ Provider created successfully');
      })
      .addCase(createProviderProfile.rejected, (state, action) => {
        state.creating = false;
        state.createError = action.payload || 'Failed to create provider';
        state.createSuccess = false;
        console.log('❌ Provider creation failed:', state.createError);
      })
      
      // ========== UPDATE PROVIDER ==========
      .addCase(updateProvider.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.currentProvider = action.payload?.data || null;
        if (action.payload?.data) {
          const index = state.providers.findIndex(p => p._id === action.payload.data._id);
          if (index !== -1) {
            state.providers[index] = action.payload.data;
          }
        }
      })
      .addCase(updateProvider.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update provider';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE PROVIDER ACTIVITY ==========
      .addCase(updateProviderActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProviderActivity.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentProvider) {
          state.currentProvider.lastActive = new Date();
        }
      })
      .addCase(updateProviderActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update activity';
      })
      
      // ========== GET MY PROVIDER ==========
      .addCase(getMyProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProvider = action.payload?.data || null;
      })
      .addCase(getMyProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get your provider profile';
      })
      
      // ========== UPDATE MY PROVIDER ==========
      .addCase(updateMyProvider.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateMyProvider.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.currentProvider = action.payload?.data || null;
        if (action.payload?.data) {
          const index = state.providers.findIndex(p => p._id === action.payload.data._id);
          if (index !== -1) {
            state.providers[index] = action.payload.data;
          }
        }
      })
      .addCase(updateMyProvider.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update your provider profile';
        state.updateSuccess = false;
      })
      
      // ========== DELETE PROVIDER ==========
      .addCase(deleteProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProvider = null;
        // Remove from list
        const providerId = action.meta?.arg;
        if (providerId) {
          state.providers = state.providers.filter(p => p._id !== providerId);
        }
      })
      .addCase(deleteProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete provider';
      })
      
      // ========== TOGGLE FEATURED ==========
      .addCase(toggleFeatured.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(toggleFeatured.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const data = action.payload?.data;
        if (data && state.currentProvider) {
          state.currentProvider.isFeatured = data.isFeatured;
          state.currentProvider.featuredUntil = data.featuredUntil;
        }
      })
      .addCase(toggleFeatured.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to toggle featured status';
      })
      
      // ========== GET PROVIDER STATISTICS ==========
      .addCase(getProviderStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProviderStatistics.fulfilled, (state, action) => {
        state.loading = false;
        // Store statistics in current provider or a separate stats object
        if (state.currentProvider) {
          state.currentProvider.statistics = action.payload?.data || null;
        }
      })
      .addCase(getProviderStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get provider statistics';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectProviderState = (state) => state.provider;

// Public selectors
export const selectAllProviders = createSelector(
  selectProviderState,
  (provider) => provider?.providers || []
);

export const selectCurrentProvider = createSelector(
  selectProviderState,
  (provider) => provider?.currentProvider || null
);

export const selectFeaturedProviders = createSelector(
  selectProviderState,
  (provider) => provider?.featuredProviders || []
);

export const selectNearbyProviders = createSelector(
  selectProviderState,
  (provider) => provider?.nearbyProviders || []
);

export const selectDiasporaProviders = createSelector(
  selectProviderState,
  (provider) => provider?.diasporaProviders || []
);

export const selectLocationInfo = createSelector(
  selectProviderState,
  (provider) => provider?.locationInfo || null
);

export const selectProviderPagination = createSelector(
  selectProviderState,
  (provider) => provider?.pagination || { total: 0, page: 1, limit: 10, totalPages: 0 }
);

export const selectProviderFilters = createSelector(
  selectProviderState,
  (provider) => provider?.filters || {}
);

export const selectProviderSearchQuery = createSelector(
  selectProviderState,
  (provider) => provider?.searchQuery || ''
);

export const selectProviderLoading = createSelector(
  selectProviderState,
  (provider) => provider?.loading || false
);

export const selectProviderCreating = createSelector(
  selectProviderState,
  (provider) => provider?.creating || false
);

export const selectProviderUpdating = createSelector(
  selectProviderState,
  (provider) => provider?.updating || false
);

export const selectProviderError = createSelector(
  selectProviderState,
  (provider) => provider?.error || null
);

export const selectProviderCreateError = createSelector(
  selectProviderState,
  (provider) => provider?.createError || null
);

export const selectProviderUpdateError = createSelector(
  selectProviderState,
  (provider) => provider?.updateError || null
);

export const selectProviderCreateSuccess = createSelector(
  selectProviderState,
  (provider) => provider?.createSuccess || false
);

export const selectProviderUpdateSuccess = createSelector(
  selectProviderState,
  (provider) => provider?.updateSuccess || false
);

export const selectHasMoreProviders = createSelector(
  selectProviderState,
  (provider) => provider?.hasMore || false
);

export const selectSelectedProviderId = createSelector(
  selectProviderState,
  (provider) => provider?.selectedProviderId || null
);

// ==================== DERIVED SELECTORS ====================

export const selectFilteredProviders = createSelector(
  [selectAllProviders, selectProviderFilters, selectProviderSearchQuery],
  (providers, filters, searchQuery) => {
    if (!Array.isArray(providers)) return [];
    
    let filtered = [...providers];
    
    // Apply filters
    if (filters.serviceType) {
      filtered = filtered.filter(p => p.serviceType === filters.serviceType);
    }
    
    if (filters.isFeatured !== undefined) {
      filtered = filtered.filter(p => p.isFeatured === filters.isFeatured);
    }
    
    if (filters.minRating) {
      filtered = filtered.filter(p => p.rating >= filters.minRating);
    }
    
    if (filters.languages && filters.languages.length > 0) {
      filtered = filtered.filter(p => 
        p.languages && p.languages.some(lang => filters.languages.includes(lang))
      );
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.businessName?.toLowerCase().includes(query) ||
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.serviceType?.toLowerCase().includes(query) ||
        p.address?.city?.toLowerCase().includes(query) ||
        p.address?.country?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }
);

export const selectProvidersByServiceType = (serviceType) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return [];
    return providers.filter(p => p.serviceType === serviceType);
  }
);

export const selectProviderById = (id) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return null;
    return providers.find(p => p._id === id || p.id === id);
  }
);

export const selectProvidersByCity = (city) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return [];
    return providers.filter(p => p.address?.city?.toLowerCase() === city.toLowerCase());
  }
);

export const selectProvidersByCountry = (country) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return [];
    return providers.filter(p => p.address?.country?.toLowerCase() === country.toLowerCase());
  }
);

export const selectProvidersByLanguage = (language) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return [];
    return providers.filter(p => p.languages && p.languages.includes(language));
  }
);

export const selectTopRatedProviders = (limit = 10) => createSelector(
  selectAllProviders,
  (providers) => {
    if (!Array.isArray(providers)) return [];
    return [...providers]
      .filter(p => p.rating && p.rating > 0)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, limit);
  }
);

// ==================== EXPORTS ====================

export const {
  resetProviderState,
  resetCreateState, 
  resetUpdateState,
  clearErrors,
  setFilters,
  clearFilters,
  setSearchQuery,
  setPagination,
  setRefreshing,
  selectProvider,
  clearCurrentProvider,
  updateProviderInList,
  removeProviderFromList,
  updateCurrentProvider
} = providerSlice.actions;

export default providerSlice.reducer;